import {
	Button,
	Dialog,
	IconButton,
	InputAdornment,
	TextField,
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import React, { useMemo, useState } from 'react';

const RULES = [
	{ key: 'length', label: 'Be at least 8 characters', test: (v: string) => v.length >= 8 },
	{ key: 'upper', label: 'Include an uppercase letter (A–Z)', test: (v: string) => /[A-Z]/.test(v) },
	{ key: 'lower', label: 'Include a lowercase letter (a–z)', test: (v: string) => /[a-z]/.test(v) },
	{ key: 'number', label: 'Include a number (0–9)', test: (v: string) => /[0-9]/.test(v) },
	{
		key: 'symbol',
		label: 'Include one of these symbols: ! @ # $ % ^ & *',
		test: (v: string) => /[!@#$%^&*]/.test(v),
	},
];

const REASONS: Record<string, string> = {
	FIRST:
		'To keep outlet information safe, every MenuX user must now set a new password.',
	ADMIN_RESET:
		'Your password was set by an administrator. Please choose your own password.',
	EXPIRED: 'Your password is more than {{days}} days old. Please choose a new one.',
};

export const ForcePasswordChangeDialog = (props: {
	open: boolean;
	reason?: string | null;
	expiryDays?: number;
	historyCount?: number;
	askCurrent?: boolean;
	loading?: boolean;
	onSubmit: (value: { currentPassword?: string; newPassword: string }) => void;
	onLogout: () => void;
}) => {
	const {
		open,
		reason,
		expiryDays,
		historyCount = 3,
		askCurrent,
		loading,
		onSubmit,
		onLogout,
	} = props;
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [show, setShow] = useState(false);
	const [error, setError] = useState('');

	const reasonText = useMemo(() => {
		const template = REASONS[reason || 'FIRST'] || REASONS.FIRST;
		return template.replace('{{days}}', String(expiryDays || 90));
	}, [reason, expiryDays]);

	const submit = () => {
		if (askCurrent && !currentPassword) {
			setError('Current password is required');
			return;
		}
		if (newPassword !== confirmPassword) {
			setError('The passwords do not match.');
			return;
		}
		if (RULES.some(rule => !rule.test(newPassword))) {
			setError('Your new password does not meet the rules.');
			return;
		}
		setError('');
		onSubmit({ currentPassword, newPassword });
	};

	return (
		<Dialog open={open} disableBackdropClick disableEscapeKeyDown fullWidth>
			<div style={{ padding: '20px 24px' }}>
				<div className="xsTitle" style={{ marginBottom: 8 }}>
					Please change your password
				</div>
				<div style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 12 }}>
					{reasonText}
				</div>
				<div style={{ fontSize: 13, marginBottom: 6 }}>Your new password must:</div>
				<ul style={{ marginTop: 0, paddingLeft: 18, fontSize: 13 }}>
					{RULES.map(rule => (
						<li
							key={rule.key}
							style={{ color: rule.test(newPassword) ? '#2e7d32' : undefined }}
						>
							{rule.label}
						</li>
					))}
					<li>
						Not be your current password or one of your last {historyCount}
					</li>
				</ul>
				<div style={{ fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
					Never share your password. MenuX and IFCA staff will never ask for it.
					After you change it, you will be signed out on your other devices.
				</div>
				{askCurrent && (
					<TextField
						fullWidth
						type={show ? 'text' : 'password'}
						label="Current Password"
						value={currentPassword}
						onChange={e => setCurrentPassword(e.target.value)}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton onClick={() => setShow(!show)}>
										{show ? <Visibility /> : <VisibilityOff />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
				)}
				<TextField
					fullWidth
					type={show ? 'text' : 'password'}
					label="New Password"
					value={newPassword}
					onChange={e => setNewPassword(e.target.value)}
				/>
				<TextField
					fullWidth
					type={show ? 'text' : 'password'}
					label="Confirm New Password"
					value={confirmPassword}
					onChange={e => setConfirmPassword(e.target.value)}
				/>
				{error && (
					<div style={{ color: '#d32f2f', fontSize: 12, marginTop: 8 }}>{error}</div>
				)}
				<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
					<Button onClick={onLogout} disabled={loading}>
						Log Out
					</Button>
					<Button
						color="primary"
						variant="contained"
						onClick={submit}
						disabled={loading}
					>
						Change Password
					</Button>
				</div>
			</div>
		</Dialog>
	);
};
